import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import EditIcon from '@mui/icons-material/Edit'
import { Box, Divider, IconButton, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'

interface AILiveElementTreeProps {
    contents: string
    pageID?: string
    onEditPage?: () => void
    onElementClick?: (elementID: string) => void
}

export function AILiveElementTree({
    contents,
    pageID,
    onEditPage,
    onElementClick,
}: AILiveElementTreeProps): JSX.Element {
    let elements: any[] = []
    try {
        const parsed = JSON.parse(contents)
        if (Array.isArray(parsed)) elements = parsed
    } catch {
        // ignore
    }

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
                    Elements
                </Typography>
                {pageID && onEditPage && (
                    <Tooltip title="Edit this page in the CMS editor">
                        <IconButton size="small" onClick={onEditPage}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            <Divider />
            {elements.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
                    No elements
                </Typography>
            ) : (
                <Box sx={{ px: 1, py: 0.5 }}>
                    {elements.map((el, i) => (
                        <ElementNode key={el.id ?? i} element={el} depth={0} onElementClick={onElementClick} />
                    ))}
                </Box>
            )}
        </>
    )
}

function ElementNode({
    element,
    depth,
    onElementClick,
}: {
    element: any
    depth: number
    onElementClick?: (id: string) => void
}): JSX.Element {
    const [expanded, setExpanded] = useState(depth < 1)
    const type = element.type ?? 'unknown'
    const data = element.data ?? {}
    const deactivated = element.deactivated === true
    const elementID = element.id

    // Extract a short preview of the element's content
    const preview = getPreview(data)

    // Find nested children: regions, items
    const children = getChildren(data)
    const hasChildren = children.length > 0

    const handleClick = () => {
        if (hasChildren) setExpanded(!expanded)
        if (elementID && onElementClick) onElementClick(elementID)
    }

    return (
        <Box sx={{ opacity: deactivated ? 0.4 : 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    pl: depth * 2,
                    py: 0.25,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 0.5,
                }}
                onClick={handleClick}
            >
                {hasChildren ? (
                    expanded ? (
                        <ExpandMoreIcon sx={{ fontSize: 16, mt: 0.25, mr: 0.5, color: 'text.secondary' }} />
                    ) : (
                        <ChevronRightIcon sx={{ fontSize: 16, mt: 0.25, mr: 0.5, color: 'text.secondary' }} />
                    )
                ) : (
                    <Box sx={{ width: 20, mr: 0.5 }} />
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            color: deactivated ? 'text.disabled' : 'text.primary',
                        }}
                        noWrap
                    >
                        {type}
                        {deactivated && ' (off)'}
                    </Typography>
                    {preview && (
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                display: 'block',
                                lineHeight: 1.3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {preview}
                        </Typography>
                    )}
                </Box>
            </Box>
            {expanded &&
                children.map((child, i) => (
                    <ElementNode
                        key={child.id ?? `${depth}-${i}`}
                        element={child}
                        depth={depth + 1}
                        onElementClick={onElementClick}
                    />
                ))}
        </Box>
    )
}

/** Extract a short text preview from element data */
function getPreview(data: any): string {
    if (!data) return ''
    // Try common text fields in priority order
    if (data.headline) return stripMarkdown(data.headline)
    if (data.title) return stripMarkdown(data.title)
    if (data.name) return stripMarkdown(data.name)
    if (data.label) return data.label
    if (data.content) return stripMarkdown(data.content).substring(0, 80)
    if (data.text) return stripMarkdown(data.text).substring(0, 80)
    if (data.description) return stripMarkdown(data.description).substring(0, 80)
    if (data.identifier) return data.identifier
    if (data.__qbd_blockId) return `Block: ${data.__qbd_blockId}`
    return ''
}

/** Strip markdown bold/italic markers for cleaner preview */
function stripMarkdown(text: string): string {
    return text.replace(/\*\*/g, '').replace(/\n/g, ' ').trim()
}

/** Extract nested child elements from regions, items, etc. */
function getChildren(data: any): any[] {
    const children: any[] = []
    // Regions: array of arrays of elements (sections)
    if (data.regions && Array.isArray(data.regions)) {
        for (const region of data.regions) {
            if (Array.isArray(region)) {
                children.push(...region.filter((el: any) => el && typeof el === 'object' && el.type))
            }
        }
    }
    // Items: array of elements (carousels, dynamic sections)
    if (data.items && Array.isArray(data.items)) {
        for (const item of data.items) {
            if (item && typeof item === 'object') {
                // Items might be elements with type, or plain data objects
                if (item.type) {
                    children.push(item)
                } else {
                    // Wrap plain data as a pseudo-element for display
                    const preview = item.title || item.name || item.headline || ''
                    if (preview) {
                        children.push({ type: 'item', data: item, id: item.id })
                    }
                }
            }
        }
    }
    return children
}
